'use client';

import { useState } from 'react';

import { DesktopSmall, MobileSmall } from '@wix/wix-ui-icons-common';
import { ChevronRight, MoreHorizontal, RotateCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';

import {
  AVATAR_TEXT_COLOR,
  RESOLVED_AVATAR_COLOR,
  getAvatarColor,
  getAvatarInitial,
} from '@/utils/avatarColor';
import {
  formatAbsoluteTime,
  formatRelativeTime,
} from '@/utils/formatRelativeTime';
import { getCommentStatusLabel } from '@/utils/getCommentStatusLabel';
import { getDeviceLabel } from '@/utils/getDeviceLabel';
import { translateDisplayName } from '@/utils/translateDisplayName';

import type { DevicePreset, Pin } from '@/types/feedback';

const DEVICE_ICONS: Partial<Record<DevicePreset, React.ReactNode>> = {
  mobile: <MobileSmall />,
  desktop: <DesktopSmall />,
};

interface SidebarPinItemProps {
  pin: Pin;
  pinNumber: number;
  commenterName?: string;
  isUnread?: boolean;
  isSelected?: boolean;
  onPinClick: (id: string) => void;
  onReopen?: () => void;
}

export function SidebarPinItem({
  pin,
  pinNumber,
  commenterName,
  isUnread = false,
  isSelected = false,
  onPinClick,
  onReopen,
}: SidebarPinItemProps) {
  const t = useTranslations();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const displayName = translateDisplayName(commenterName, t);
  const statusLabel = getCommentStatusLabel(pin.status, t);
  const isClickable =
    pin.status !== 'RESOLVED' &&
    (Boolean(pin.isLegacy) ||
      (pin.offsetXPct != null && pin.offsetYPct != null));

  const handleItemKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
    if (!isClickable) return;
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    onPinClick(pin.id);
  };

  return (
    <li
      data-hook="sidebar-pin-item"
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? () => onPinClick(pin.id) : undefined}
      onKeyDown={isClickable ? handleItemKeyDown : undefined}
      className={`relative group space-y-2 px-4 py-3${
        isClickable
          ? ` cursor-pointer transition-colors focus:outline-none${isSelected ? ' bg-blue-50' : ' hover:bg-blue-50'}`
          : ''
      }`}
      aria-label={
        isClickable
          ? t('get-feedback-sidebarPin-scrollToPin', { pinNumber })
          : undefined
      }
    >
      {/* Device label above name row */}
      {pin.devicePreset && DEVICE_ICONS[pin.devicePreset] && (
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <span className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
            {DEVICE_ICONS[pin.devicePreset]}
          </span>
          {getDeviceLabel(pin.devicePreset, t)}
        </span>
      )}

      {/* Row: avatar + name + timestamp + unread dot */}
      <div className="flex items-center gap-2">
        <span
          data-hook="sidebar-comment-avatar"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
          style={{
            backgroundColor:
              pin.status === 'RESOLVED'
                ? RESOLVED_AVATAR_COLOR
                : getAvatarColor(commenterName),
            color: AVATAR_TEXT_COLOR,
          }}
        >
          {getAvatarInitial(commenterName)}
        </span>
        <span
          data-hook="sidebar-pin-commenter-name"
          className={`text-xs font-semibold text-foreground leading-tight truncate${pin.status === 'RESOLVED' ? ' opacity-50' : ''}`}
        >
          {displayName}
        </span>
        <span
          className={`flex-shrink-0 text-[11px] text-muted-foreground leading-tight${pin.status === 'RESOLVED' ? ' opacity-50' : ''}`}
          title={formatAbsoluteTime(pin.createdAt)}
        >
          {formatRelativeTime(pin.createdAt, t)}
        </span>
        {isUnread && (
          <span
            data-hook="sidebar-pin-unread-dot"
            className="ml-auto h-2 w-2 flex-shrink-0 rounded-full bg-primary"
          />
        )}
        {pin.status === 'RESOLVED' && onReopen && (
          <div className="relative ml-auto">
            <button
              type="button"
              aria-label={t('get-feedback-pinPopover-moreOptions')}
              onClick={(event) => {
                event.stopPropagation();
                setIsMenuOpen((open) => !open);
              }}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 min-w-[120px] rounded-lg border border-border bg-background py-1 shadow-lg">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsMenuOpen(false);
                    onReopen();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-muted"
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('get-feedback-pinPopover-reopen')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {pin.label && (
        <p
          className={`text-xs text-foreground leading-relaxed${pin.status === 'RESOLVED' ? ' opacity-50' : ''}`}
        >
          {pin.label}
        </p>
      )}

      {/* Row 3: pin number + status badge + reply count | dot */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {pin.status === 'RESOLVED' && (
            <span className="inline-flex items-center gap-1 text-[11px] text-green-600">
              <svg
                width="11"
                height="8"
                viewBox="0 0 11 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.70711 3.29289C1.31658 2.90237 0.683418 2.90237 0.292893 3.29289C-0.0976311 3.68342 -0.0976311 4.31658 0.292893 4.70711L3.29289 7.70711C3.68342 8.09763 4.31658 8.09763 4.70711 7.70711L10.7071 1.70711C11.0976 1.31658 11.0976 0.683418 10.7071 0.292893C10.3166 -0.0976311 9.68342 -0.0976311 9.29289 0.292893L4 5.58579L1.70711 3.29289Z"
                  fill="var(--resolved-color)"
                />
              </svg>
              {statusLabel}
            </span>
          )}
          {(pin.replies?.length ?? 0) > 0 && (
            <>
              {pin.status === 'RESOLVED' && (
                <span className="h-3 w-px bg-border" />
              )}
              <span className="text-[11px] text-blue-700">
                {t('get-feedback-sidebarPin-replyCount', {
                  count: pin.replies!.length,
                })}
              </span>
            </>
          )}
        </div>
      </div>

      {isClickable && (
        <div
          className="pointer-events-none absolute inset-y-0 right-0 flex w-16 items-center justify-end pr-3 opacity-0 transition-opacity group-hover:opacity-100"
          style={{
            background: 'linear-gradient(to right, transparent, #EFF6FF 55%)',
          }}
        >
          <ChevronRight className="h-4 w-4 text-blue-700" />
        </div>
      )}
    </li>
  );
}
